import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reportsResponseSchema } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';
import { formatDate } from '../issuance/FormsPage.js';
import { StatusPill } from './GearCard.js';

/**
 * Open loss and damage reports in the caller's reach (REQUIREMENTS §2.6
 * "commander alerts"). Read-only on purpose: closing a report — found or
 * written off — is an action on the soldier's gear, taken on their page,
 * so the person closing it sees everything else that soldier holds.
 */
export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const state = useApi('/reports', reportsResponseSchema);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  const { assignments, soldiers } = state.data;
  const soldier = (uid: string) => soldiers.find((s) => s.uid === uid);

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="reports-page">
      <h2 className="text-lg font-semibold text-slate-900">{t('reports.title')}</h2>
      <p className="mt-1 text-sm text-slate-500">{t('reports.hint')}</p>
      {assignments.length === 0 && <p className="mt-3 text-sm text-slate-500" data-testid="reports-empty">{t('reports.empty')}</p>}
      <ul className="mt-3 divide-y divide-slate-100">
        {assignments.map((a) => {
          const s = soldier(a.soldierUid);
          return (
            <li key={a.id} className="flex flex-wrap items-start justify-between gap-2 py-3" data-testid="report-row" data-status={a.status}>
              <div>
                <p className="font-medium text-slate-900">
                  {i18n.language.startsWith('en') ? a.nameEn : a.nameHe}
                  {a.serialNumber && (
                    <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{a.serialNumber}</span>
                  )}
                </p>
                <p className="text-sm text-slate-600">
                  {s?.name ?? a.soldierUid}
                  {s?.unitName && <span className="text-slate-400"> · {s.unitName}</span>}
                </p>
                {a.report && <p className="mt-1 text-sm text-slate-700">{a.report.notes}</p>}
              </div>
              <div className="flex flex-col items-end gap-1 text-xs text-slate-500">
                <StatusPill status={a.status} />
                {a.report?.filedAt && <span className="latin-inline">{formatDate(a.report.filedAt, i18n.language)}</span>}
                {a.teamId && (
                  <Link to={`/org/${a.teamId ?? a.platoonId}/soldier/${a.soldierUid}`} className="text-slate-700 underline-offset-4 hover:underline" data-testid="report-open-soldier">
                    {t('reports.openSoldier')}
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
